import { memo } from 'react'

import { Box, Center, Stack, Text } from '@mantine/core'

import { IconArrowDown } from '@repo/common/components/Icons'
import {
  countFormatter,
  percentage_formatter,
} from '@repo/common/helpers/formatters'
import { useBuyFund } from '@repo/common/queries/funds'
import type { FundSymbol } from '@repo/common/queries/products'

import { ContentBox } from '@/components/ContentBox'
import {
  type BasePanelProps,
  classes,
  FlowButton,
  normalize_progress,
  NumberRow,
  RatioAndDisclosures,
  RewardsRowBox,
  type StepsType,
  useFlowControl,
} from '@/flows/flow_helpers'
import {
  type ExtraAuthSyncingProps,
  PanelAuthSyncing,
  useSkipAuthSyncing,
} from '@/panels/PanelAuthSyncing'
import {
  PanelCheckWalletRisk,
  useSkipWalletRisk,
} from '@/panels/PanelCheckWalletRisk'
import {
  PanelConfirmEmail,
  useSkipConfirmEmail,
} from '@/panels/PanelConfirmEmail'
import {
  type ExtraIdentificationProps,
  PanelIdentification,
  useSkipIdentification,
} from '@/panels/PanelIdentification'
import {
  PanelLocationCheck,
  useSkipLocationCheck,
} from '@/panels/PanelLocationCheck'
import {
  type ExtraPanelPermitProps,
  PanelPermit2,
  useSkipPermit2,
} from '@/panels/PanelPermit2'
import {
  PanelRegisterWallet,
  useRegisterWalletTitle,
  useSkipRegisterWallet,
} from '@/panels/PanelRegisterWallet'
import { PanelSignup, useSkipLogin } from '@/panels/PanelSignup'

import { useMintFields } from './useMintFields'

type MintPanelProps = BasePanelProps &
  ExtraAuthSyncingProps &
  ExtraIdentificationProps &
  ExtraPanelPermitProps

const steps: StepsType<MintPanelProps> = normalize_progress([
  {
    title: 'Signup',
    Panel: PanelSignup,
    progress: 10,
    useSkipCheck: useSkipLogin,
  },
  {
    title: 'Confirm Email',
    Panel: PanelConfirmEmail,
    progress: 15,
    useSkipCheck: useSkipConfirmEmail,
  },
  {
    title: 'Location Check',
    Panel: PanelLocationCheck,
    progress: 5,
    useSkipCheck: useSkipLocationCheck,
  },
  {
    title: useRegisterWalletTitle,
    Panel: PanelRegisterWallet,
    progress: 5,
    useSkipCheck: useSkipRegisterWallet,
  },
  {
    title: 'Wallet Check',
    Panel: PanelCheckWalletRisk,
    progress: 5,
    useSkipCheck: useSkipWalletRisk,
  },
  {
    title: 'Identification',
    Panel: PanelIdentification,
    progress: 30,
    useSkipCheck: useSkipIdentification,
  },
  {
    title: 'Enable Permit2',
    Panel: PanelPermit2,
    progress: 5,
    useSkipCheck: useSkipPermit2,
  },
  {
    title: 'Auth Syncing',
    Panel: PanelAuthSyncing,
    progress: 5,
    useSkipCheck: useSkipAuthSyncing,
  },
])

export const FundMint = memo<{
  fund_symbol: FundSymbol
  starting_tokens_in?: number
  onCompleted: () => void
  // eslint-disable-next-line max-lines-per-function, complexity
}>(function FundMint({ fund_symbol, starting_tokens_in, onCompleted }) {
  const {
    form,
    balance_USDC,
    balance_fund,
    wallet_isConnected,
    form_valid,
    paused,
    is_loading,
    error_reason,
    tokens_in_focus,
    tokens_out_focus,
    fund_price,
    fisn_rewards,
    allowed_kyc_levels,
    level,
    deposit_limit,
  } = useMintFields({ fund_symbol, starting_tokens_in })

  const buy_query = useBuyFund({ symbol: fund_symbol })

  const { submitted, onSubmit, modal } = useFlowControl({
    steps,
    extra_args: {
      symbol_in: 'USDC',
      allowed_kyc_levels,
      action_term: 'minting',
      level,
    },
    final_action: () =>
      buy_query.mutateAsync(form.getValues()).then(onCompleted),
  })

  return (
    <Stack maw="100%">
      {modal}
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack
          maw="calc(100vw - 2 * var(--app-body-margin))"
          w="500px"
          gap="xxs"
        >
          <Box pos="relative">
            <Box className={classes.down_box}>
              <Center h="100%">
                <IconArrowDown
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </Center>
            </Box>
            <ContentBox
              p="lg"
              gap="xs"
              ref={tokens_in_focus.ref}
              mod={{
                focused: tokens_in_focus.focused,
                error: Boolean(form.getInputProps('tokens_in').error),
              }}
              data-testid="USDC_container"
            >
              <Text size="md" lh="1" opacity={0.7}>
                Sell
              </Text>
              <NumberRow
                {...form.getInputProps('tokens_in', { withError: false })}
                disabled={is_loading || submitted}
                aria-label="sell tokens"
                symbol="USDC"
                balance_number={balance_USDC}
                max_button={() => form.setFieldValue('tokens_in', balance_USDC)}
              />
            </ContentBox>
          </Box>
          <ContentBox
            p="lg"
            gap="xs"
            ref={tokens_out_focus.ref}
            mod={{ focused: tokens_out_focus.focused }}
            data-testid="Fund_container"
          >
            <Text size="md" lh="1" opacity={0.7}>
              Mint
            </Text>
            <NumberRow
              {...form.getInputProps('tokens_out', { withError: false })}
              disabled={is_loading || submitted}
              aria-label="mint tokens"
              symbol={fund_symbol}
              balance_number={balance_fund}
              balance_maximumFractionDigits={4}
            />
          </ContentBox>
          <FlowButton
            wallet_isConnected={wallet_isConnected}
            form_valid={form_valid}
            is_loading={is_loading}
            paused={paused}
            error_reason={error_reason}
            mutation_query={buy_query}
            action_word="Mint"
          />
          <RewardsRowBox message="Free Mint Rewards" {...fisn_rewards} />
          <RatioAndDisclosures
            symbol={fund_symbol}
            price={fund_price}
            maximumFractionDigits={6}
          >
            <Text size="sm">
              Max slippage:{' '}
              {percentage_formatter(form.getValues().slippage_percentage)}
            </Text>
            <Text size="sm">
              Deposit Limit:{' '}
              {countFormatter(deposit_limit ?? 0, {
                maximumFractionDigits: 2,
                compact: false,
              })}{' '}
            </Text>
          </RatioAndDisclosures>
        </Stack>
      </form>
    </Stack>
  )
})
